import { useFetch, CachePolicies } from 'use-http';
import { getToken, keycloak } from '@coverright/shared/keycloak';
import {
  ChatAnswer,
  ChatUserContext,
  HighlightsResponse,
  PageContext,
  SessionInitResponse,
} from './chat-ai-types';
import { createChatSession } from './chat-stream';
import { useMedicareQuote } from '../medicare/use-medicare-quote';
import { useUserProfile } from '../enrollment/use-user-profile';
import { useAppContext } from '@coverright/shared/contexts';
import { logEvent } from '@coverright/shared/analytics';
import { useMemo } from 'react';
import { covertDateToSeverFormat } from '@coverright/utils';
import { environment } from '../../../../environments/environment';

export const useChatAi = () => {
  const quote = useMedicareQuote();
  const profile = useUserProfile();
  const [{ benefits, paymentPreference, hasDrugs, hasDoctors, situation, programs }] = useAppContext();

  const userContext: ChatUserContext = useMemo(() => ({
    zip: profile?.zip || undefined,
    dob: profile?.birthDate ? covertDateToSeverFormat(profile.birthDate) as string : undefined,
    gender: profile?.gender || undefined,
    tobacco: profile?.tobacco ?? undefined,
    enrollment_status: situation || undefined,
    cost_style: paymentPreference || undefined,
    has_drugs: hasDrugs,
    has_doctors: hasDoctors,
    benefit_preferences: benefits || undefined,
    special_programs: programs || undefined,
    doctors: quote?.preferredDoctors.map(d => ({
      npi: d.npi,
      name: d.name,
      specialty: d.addresses?.at(0)?.specialty,
      zip: d.addresses?.at(0)?.addressLine2?.match(/\d{5}/)?.[0],
    })),
    drugs: quote?.preferredDrugs.map(d => ({
      rxcui: d.packRxcui,
      name: d.packName,
    })),
  }), [quote, profile, paymentPreference, benefits, hasDrugs, hasDoctors, situation, programs])

  const {post, error, response, loading} = useFetch(environment.chatAiUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
    interceptors: {
      request: async ({ options }) => {
        if (keycloak.authenticated) {
          const token = await getToken();
          if (token) {
            options.headers = {
              ...options.headers,
              Authorization: `Bearer ${token}`,
            };
          }
        }
        return options;
      },
    },
  })

  const highlights = async (bidIds: string[]): Promise<HighlightsResponse | undefined> => {
    const res = await post('/plans/highlights', { bid_ids: bidIds });
    if (response.ok) return res as HighlightsResponse;
  }

  const initSession = async (context: PageContext): Promise<SessionInitResponse | boolean> => {
    const res = await post('/sessions/init', {
      ...context,
      user_context: userContext,
    });
    if (response.ok && context.page_context === 'recommended_plans') return res as SessionInitResponse;
    return response.ok;
  }

  const chat = async (question: string): Promise<ChatAnswer> => {
    logEvent('ai_chat_question', { question })
    return await post('/chat', { question })
  }

  return {
    loading,
    highlights,
    initSession,
    chat,
    createChatSession,
  }
}
